import { Link } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import { usePageMeta } from '@/hooks/usePageMeta';
import { BRAND_NAME } from '@/mocks/categories';

const scopes = [
  { icon: 'ri-film-line', title: '电影', desc: '剧情、动作、爱情、悬疑、科幻等题材的国语对白电影资源。' },
  { icon: 'ri-tv-line', title: '电视剧', desc: '都市、古装、悬疑、家庭等类型的国语对白剧集资源。' },
  { icon: 'ri-mic-line', title: '综艺', desc: '真人秀、脱口秀、音乐舞台等轻松有趣的国语对白综艺。' },
  { icon: 'ri-earth-line', title: '纪录片', desc: '自然、人文、历史视角的国语对白纪录片资源。' },
];

const values = [
  { icon: 'ri-layout-grid-line', title: '分类清晰', desc: '按类型与题材精细分类，多维度筛选快速定位内容。' },
  { icon: 'ri-search-eye-line', title: '检索高效', desc: '支持名称、主演、关键词模糊搜索，结果实时联动筛选。' },
  { icon: 'ri-shield-check-line', title: '信息真实', desc: '统一数据源展示，卡片与详情信息保持一致。' },
  { icon: 'ri-heart-line', title: '尊重版权', desc: '本站仅作资源展示与导航，倡导支持正版影视作品。' },
];

export default function AboutPage() {
  usePageMeta({
    title: `关于我们 - ${BRAND_NAME}`,
    description: `${BRAND_NAME}是一个聚焦国语对白影视及影音内容的信息展示平台，介绍本站定位、内容范围与服务理念，帮助用户了解多样国语影音作品。`,
    keywords: `关于我们,${BRAND_NAME},国语对白,影音资源,影视展示`,
  });

  return (
    <PageShell>
      <div className="bg-primary-900 text-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14 md:py-20">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-background-100 text-xs">
            <i className="ri-information-line"></i>
            关于本站
          </span>
          <h1 className="mt-4 text-3xl md:text-4xl font-bold text-background-50">关于 {BRAND_NAME}</h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base text-background-200 leading-relaxed">
            {BRAND_NAME}是一个聚焦国语对白影视及影音内容的信息展示与导航平台，
            面向国语影视爱好者、影音资源搜集者与普通观众，帮助用户快速、准确地查找并了解丰富的国语影音作品。
          </p>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-12 md:py-16 space-y-14">
        <section>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-1 h-7 rounded-full bg-accent-500"></span>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">品牌介绍</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 text-sm md:text-base text-foreground-700 leading-loose space-y-4">
              <p>
                {BRAND_NAME}以「现代门户」的阅读体验为核心，围绕国语对白影视及影音内容，
                构建了从分类浏览、多维筛选、关键词搜索到资源详情的完整浏览路径，
                让影音资源的查找与了解变得简单直观。
              </p>
              <p>
                我们相信，好的内容导航能帮用户少走弯路。无论是想重温一部经典，
                还是想随手发现一部新作，都能在这里快速找到线索。
              </p>
              <p className="text-foreground-500">
                提示：本站资源信息均为集中展示与导航，播放链接仅作界面演示，不具备真实播放能力。
              </p>
            </div>
            <div className="p-6 bg-background-100 border border-background-200 rounded-lg">
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <p className="font-heading text-3xl font-bold text-primary-500">20+</p>
                  <p className="text-sm text-foreground-600 mt-1">收录资源</p>
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-accent-500">4</p>
                  <p className="text-sm text-foreground-600 mt-1">内容大类</p>
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-secondary-500">15</p>
                  <p className="text-sm text-foreground-600 mt-1">细分分类</p>
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-foreground-900">7</p>
                  <p className="text-sm text-foreground-600 mt-1">覆盖地区</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-1 h-7 rounded-full bg-accent-500"></span>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">内容范围</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {scopes.map((s) => (
              <div key={s.title} className="p-5 bg-background-50 border border-background-200 rounded-lg">
                <span className="w-11 h-11 rounded-md bg-primary-50 text-primary-500 flex items-center justify-center">
                  <i className={`${s.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 font-semibold text-foreground-950">{s.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-1 h-7 rounded-full bg-accent-500"></span>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">服务理念</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {values.map((v) => (
              <div key={v.title} className="p-5 bg-background-50 border border-background-200 rounded-lg">
                <span className="w-11 h-11 rounded-md bg-secondary-100 text-secondary-600 flex items-center justify-center">
                  <i className={`${v.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 font-semibold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="p-6 bg-background-100 border border-background-200 rounded-lg">
            <h2 className="text-lg font-bold text-foreground-950">团队与联系方式</h2>
            <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
              我们是一支专注内容整理与前端体验的小团队，持续优化分类体系与检索能力。
              如对本站有任何建议或合作意向，欢迎随时联系。
            </p>
            <div className="mt-4 space-y-2 text-sm text-foreground-700">
              <p className="flex items-center gap-2">
                <i className="ri-mail-line text-accent-500"></i>
                service@gyyy-resource.cn
              </p>
              <p className="flex items-center gap-2">
                <i className="ri-time-line text-accent-500"></i>
                服务时间：周一至周五 9:00 - 18:00
              </p>
            </div>
            <Link
              to="/contact"
              className="mt-5 inline-flex items-center gap-1.5 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-mail-send-line"></i>
              前往留言
            </Link>
          </div>

          <div className="p-6 bg-background-50 border border-background-200 rounded-lg">
            <h2 className="text-lg font-bold text-foreground-950">版权声明与使用须知</h2>
            <ul className="mt-3 space-y-2 text-sm text-foreground-700 leading-relaxed list-disc pl-5">
              <li>本站为影音资源信息展示与导航平台，不提供也不存储任何影视文件。</li>
              <li>页面内播放区域仅为界面演示，无真实播放能力，不产生任何播放行为。</li>
              <li>站内涉及的影视名称、剧照等信息仅用于内容介绍，版权归各自权利人所有。</li>
              <li>请通过正规渠道观看正版影视作品，本站不承担因使用所产生的相关责任。</li>
              <li>如本站内容侵犯了您的权益，请通过联系方式告知，我们将及时处理。</li>
            </ul>
          </div>
        </section>
      </div>
    </PageShell>
  );
}